import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Props = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  panel?: string;
  top?: boolean;
};

export default function FadeModal({ open, onClose, children, wide, panel, top }: Props) {
  const stash = useRef<ReactNode>(children);
  if (open) stash.current = children;
  const [alive, setAlive] = useState(open);
  const [phase, setPhase] = useState<"in" | "out">(open ? "in" : "out");

  useEffect(() => {
    if (open) {
      setAlive(true);
      setPhase("in");
      return;
    }
    setPhase("out");
    const id = window.setTimeout(() => setAlive(false), 220);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!alive) return null;

  return createPortal(
    <div
      className={`pr-modal fixed inset-0 flex items-end justify-center p-4 sm:items-center ${top ? "z-[60]" : "z-50"} ${phase === "out" ? "is-out" : "is-in"}`}
      onMouseDown={onClose}
      role="presentation"
    >
      <div
        className={`pr-noscroll max-h-[92vh] w-full ${panel || (wide ? "max-w-6xl" : "max-w-lg")}`}
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {open ? children : stash.current}
      </div>
    </div>,
    document.body,
  );
}
